/* eslint-disable no-unused-vars */
/* eslint-disable max-len */
/* eslint-disable react/prop-types */

import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Spin,
  message,
} from 'antd';
import React, { useEffect, useState } from 'react';
import Select from 'components/Select';
import Table from 'components/Table';
import { formatReceived } from 'utils/formatValues';
import { CheckCircleOutlined } from '@ant-design/icons';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
  ESTATUS_INACTIVO,
} from 'utils/constants';

import { getEstadosGlobales } from 'api/catalogos/index';
import { checkToken } from 'api/utils';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Moment from 'moment';
import { onError, onSuccess } from 'utils/handlers';
import ModalConfirmation from 'components/ModalConfirmation';
import { id } from 'date-fns/locale';
import {
  autorizarFuenteDeReclutamiento,
  deleteFuenteDeReclutamiento, getFuentesDeReclutamiento, patchFuenteDeReclutamiento, postFuenteDeReclutamiento,
} from 'api/catalogos/FuentesDeReclutamiento';

export const fuentesDeReclutamientoPermission = {
  permissionModel: 'fuentedereclutamiento',
};

const FuentesDeReclutamiento = ({ permission }) => {
  // ============================ Estados y formularios ============================
  const [form] = Form.useForm();
  const [formDatos] = Form.useForm();
  const [formInstances] = useState([form, formDatos]);

  const [open, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [disableFields, setDisableFields] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);

  // ============================ Funciones de carga de datos ============================

  // Carga general de motivos de baja (usado después de crear, editar o borrar)
  const fetchData = async () => {
    setLoading(true);
    const _data = await getFuentesDeReclutamiento();
    setData(formatReceived(_data));
    setLoading(false);
  };

  // useEffect inicial para cargar catálogos y motivos
  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getFuentesDeReclutamiento(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setLoading(false);
        setData(_data);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  // ============================ Configuración de tabla ============================

  // Configuración de columnas de la tabla
  const columns = [
    {
      titleText: 'Fuentes de Reclutamiento',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 300,
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
      width: 400,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];
  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };
  const onClickEdit = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    if (!([1].includes(key))) {
      const match = data?.find((item) => item.id === key);
      setTimeout(() => {
        if (match) {
          form.resetFields();
          setTimeout(() => {
            form.setFieldsValue(formatReceived(match));
            setDisableFields(false);
            setVisible(true);
          });
        }
      });
    } else {
      message.warn('Este registro no es editable');
    }
    setLoading(false);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchFuenteDeReclutamiento(key, values) : postFuenteDeReclutamiento(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteFuenteDeReclutamiento(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar un Nivel de Estudios ya autorizado');
        return;
      }
      const saved = await autorizarFuenteDeReclutamiento(key);
      if (saved) {
        const _data = await getFuentesDeReclutamiento();
        setData(formatReceived(_data));
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };
  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    form.resetFields();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  // ============================ Validación ============================

  const requiredRule = {
    required: true,
    message: 'Este campo es obligatorio',
  };

  const rules = {
    required: [requiredRule],
    concepto: [requiredRule],
  };

  // ============================ Render ============================

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: 'fuentedereclutamiento', action: 'autorizar' },
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              disabledBySelection: true,
              disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
              className: 'success',
            },
          ]}
          disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Nivel de Estudio`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            initialValues={{
              estados_globales: ESTATUS_ACTIVO,
            }}
          >
            <Col spam={24}>
              <Form.Item
                name="descripcion"
                label="Descripción"
                rules={[{ required: true, message: 'El campo es requerido' }]}
                hasFeedback
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="observaciones"
                label="Observaciones"
                hasFeedback
                rules={rules.required}
              >
                <Input dataSource={data} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="El registro seleccionado"
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar la Fuente de Financiamiento${selectedData?.descripcion ? `: ${selectedData.descripcion}` : ''}`}
          open={visibleAutorizar}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default FuentesDeReclutamiento;
